import React, { useEffect, useState, useMemo } from "react";
import { useCurrent, useFetch } from "../../../hooks";
import IncomesListItem from "./IncomesListItem";
import {
  MwInputText,
  MwSelector,
  MwSpinnerButton,
  NoDataFound,
  TagList,
  Pagination,
} from "../../../components/ui";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const IncomesList = () => {
  const [t] = useTranslation("global");
  const params = useParams();
  const {
    currentTenantId,
    currentCompanyId,
    currentBranchId,
    userId,
    currentLangId,
  } = useCurrent();
  const canFetchContext = Boolean(
    currentTenantId && currentCompanyId && currentBranchId
  );
  const baseApiUrl = canFetchContext
    ? `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/incomes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
    : null;
  const { data: incomesTreasuriesResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/treasuries/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );
  const { data: taxesResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_VAT_WEB_BASE_URL}/sales/taxes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );
  const [treasuiesList, setTreasuiesList] = useState([""]);

  const {
    data: incomesResult,
    loading: loadingResult,
    refreshHandler,
  } = useFetch(baseApiUrl, { cache: false, persist: false });

  const taxTypeById = useMemo(() => {
    const list = taxesResult?.data || [];
    return list.reduce((acc, tax) => {
      acc[String(tax.id)] = {
        label: currentLangId === "ar" ? tax.tax_name_ar : tax.tax_name_en,
        value: Number(tax.tax_value),
      };
      return acc;
    }, {});
  }, [taxesResult, currentLangId]);

  const [filters, setFilters] = useState({
    tagList: [],
    tDate: "",
    fDate: "",
    treasury: "",
    code: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const filteredData = useMemo(() => {
    if (!incomesResult?.data) return [];

    const sortedData = [...incomesResult.data].sort((a, b) => {
      const dateA = new Date(a?.incomes_date || 0).getTime();
      const dateB = new Date(b?.incomes_date || 0).getTime();
      if (dateA !== dateB) return dateB - dateA;
      const codeA = Number(a?.code || 0);
      const codeB = Number(b?.code || 0);
      if (!Number.isNaN(codeA) && !Number.isNaN(codeB) && codeA !== codeB) {
        return codeB - codeA;
      }
      return Number(b?.id || 0) - Number(a?.id || 0);
    });

    return sortedData.filter((item) => {
      const codeText = String(item?.code || "").toLowerCase();
      const descText = String(item?.desc || "").toLowerCase();
      const searchText = String(filters.code || "").trim().toLowerCase();
      return (
        (!searchText ||
          codeText.includes(searchText) ||
          descText.includes(searchText)) &&
        (!filters.fDate || item.incomes_date >= filters.fDate) &&
        (!filters.tDate || item.incomes_date <= filters.tDate) &&
        (!filters.treasury?.value ||
          filters.treasury.value === "all" ||
          String(item.treasury_id) === String(filters.treasury?.value)) &&
        (filters.tagList.length === 0 ||
          filters.tagList.every((tag) => item.tags.includes(tag)))
      );
    });
  }, [incomesResult, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleFilterChange = (key, value) => {
    setCurrentPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (incomesTreasuriesResult) {
      const _list = [
        {
          label: currentLangId === "ar" ? "الكل" : "All",
          value: "all",
          lang: currentLangId,
        },
        ...incomesTreasuriesResult.data.map((item) => ({
          label: currentLangId === "ar" ? item.name_ar : item.name_en,
          value: item.id,
          lang: currentLangId,
        })),
      ];

      setTreasuiesList(_list);

      if (filters.treasury?.value === "all") {
        setFilters((prevFilters) => ({
          ...prevFilters,
          treasury: _list[0],
        }));
      }
    }
  }, [incomesTreasuriesResult, currentLangId]);

  return (
    <div className="main flex flex-row relative justify-between">
      {loadingResult ? (
        <div className="loading w-full h-[70vh] absolute flex justify-center items-center">
          <MwSpinnerButton label={""} isFullCenter={true} />
        </div>
      ) : (
        <>
          {incomesResult?.data.length > 0 ? (
            <div className="flex w-full justify-center items-center flex-col ">
              <div className="filter bg-[#F8FAFC] mt-0 px-4 py-4 flex flex-wrap justify-between gap-3 pb-6 w-full rounded-md">
                <div className="search relative w-full md:w-80 ">
                  <MwInputText
                    inputType="text"
                    label={t("Filter.search")}
                    placeholder="ابحث بالسند أو الوصف"
                    value={filters.code}
                    onChange={(e) => handleFilterChange("code", e.target.value)}
                  />
                </div>
                <div className="flex justify-center items-center gap-2">
                  <div className="w-full md:w-48 ">
                    <MwInputText
                      inputType="date"
                      label={t("Filter.fDate")}
                      value={filters.fDate}
                      onChange={(e) =>
                        handleFilterChange("fDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="w-full md:w-48">
                    <MwInputText
                      inputType="date"
                      label={t("Filter.tDate")}
                      value={filters.tDate}
                      onChange={(e) =>
                        handleFilterChange("tDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="w-full md:w-48">
                    <MwSelector
                      label={t("Salaries.Treasury")}
                      initalValue={t("invoice.selectTreasury")}
                      dataType={"labelValue"}
                      _data={treasuiesList}
                      withAddNew={false}
                      withSearch={false}
                      invalid={""}
                      invalidMsg={""}
                      selectedItem={filters.treasury}
                      setSelectedItem={(val) =>
                        handleFilterChange("treasury", val)
                      }
                    />
                  </div>

                  <div className="md:w-48">
                    <TagList
                      tagList={filters.tagList}
                      setTagList={(tags) => handleFilterChange("tagList", tags)}
                      label={t("Filter.Tags")}
                      withLabel={true}
                    />
                  </div>
                </div>
              </div>
              {filteredData.length > 0 ? (
                <div className="w-[100%] mx-auto mt-2">
                  <>
                    <div
                      dir={currentLangId === "ar" ? "rtl" : "ltr"}
                      className="w-[98%] mx-auto max-h-[calc(100vh-290px)] overflow-y-auto thin-scrollbar rounded-lg"
                    >
                      <div className="sticky top-0 z-10 flex w-full gap-1 bg-[#EBF9D9] py-1 px-1 rounded-t-lg text-[#261759] font-bold text-sm">
                        <div className="w-[110px] shrink-0 text-start ps-2">
                          {t("Salaries.BondNumber")}
                        </div>
                        <div className="flex-[1.6] min-w-[280px] text-start">
                          {t("Salaries.bondDescription")}
                        </div>
                        <div className="w-[135px] shrink-0 text-center">
                          {t("Salaries.BondAmountwithoutvat")}
                        </div>
                        <div className="flex-[1.1] min-w-[220px] text-center">
                          {t("expenses.Vattype")}
                        </div>
                        <div className="w-[95px] shrink-0 text-center">
                          {t("expenses.Vatexp")}
                        </div>
                        <div className="w-[135px] shrink-0 text-center">
                          {t("Salaries.BondAmountwithvat")}
                        </div>
                        <div className="w-[122px] shrink-0 text-center">
                          {t("Salaries.BondDate")}
                        </div>
                        <div className="w-[96px] shrink-0 text-center"></div>
                      </div>
                      {currentItems.map((item, index) => (
                        <IncomesListItem
                          key={item.id}
                          item={item}
                          index={(currentPage - 1) * itemsPerPage + index}
                          refreshHandler={refreshHandler}
                          tenant={params.tenant}
                          taxTypeById={taxTypeById}
                        />
                      ))}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </>
                </div>
              ) : (
                <div className="  h-[calc(100vh-238px)] mt-[-20px]  w-[98%] mx-auto ">
                  <NoDataFound msg={t("Salaries.validate")} />
                </div>
              )}
            </div>
          ) : (
            <div className="  h-[calc(100vh-158px)]  w-[98%] mx-auto ">
              <NoDataFound msg={t("Salaries.validate")} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IncomesList;
